import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Menu, X } from 'lucide-react';

const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCallClick = () => {
    window.location.href = 'tel:[TEL]';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:contact@[MARQUE].fr';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen">
      {/* Top Contact Bar */}
      <div className="bg-gray-900 text-white py-2 px-4 text-sm">
        <div className="container flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>[TEL]</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>contact@[MARQUE].fr</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Cugnaux, Toulouse & Occitanie</span>
            </div>
          </div>
          <div className="text-yellow-400 font-semibold">
            Devis gratuit ≤30 km · Réponse 24–48h
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white'
      }`}>
        <div className="container">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="text-2xl font-bold text-gray-900 font-['Outfit']">
                [MARQUE]
              </div>
              <div className="ml-2 text-sm text-gray-600">
                Couvreur Toulouse
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className={`font-medium transition-colors duration-200 ${
                  isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Accueil
              </Link>
              <Link 
                to="/services"
                className={`font-medium transition-colors duration-200 ${
                  isActive('/services') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Services
              </Link>
              <Link 
                to="/notre-methode"
                className={`font-medium transition-colors duration-200 ${
                  isActive('/notre-methode') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Notre méthode
              </Link>
              <Link 
                to="/avis-clients"
                className={`font-medium transition-colors duration-200 ${
                  isActive('/avis-clients') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Avis clients
              </Link>
              <Link 
                to="/contact"
                className={`font-medium transition-colors duration-200 ${
                  isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Contact
              </Link>
              <button 
                onClick={handleCallClick}
                className="btn btn-primary"
              >
                Demander un devis
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600 p-2"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/"
                  className={`text-left font-medium transition-colors duration-200 px-4 py-2 ${
                    isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Accueil
                </Link>
                <Link 
                  to="/services"
                  className={`text-left font-medium transition-colors duration-200 px-4 py-2 ${
                    isActive('/services') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link 
                  to="/notre-methode"
                  className={`text-left font-medium transition-colors duration-200 px-4 py-2 ${
                    isActive('/notre-methode') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Notre méthode
                </Link>
                <Link 
                  to="/avis-clients"
                  className={`text-left font-medium transition-colors duration-200 px-4 py-2 ${
                    isActive('/avis-clients') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Avis clients
                </Link>
                <Link 
                  to="/contact"
                  className={`text-left font-medium transition-colors duration-200 px-4 py-2 ${
                    isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>
                <div className="px-4">
                  <button 
                    onClick={() => {
                      handleCallClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="btn btn-primary w-full"
                  >
                    Demander un devis
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="container">
          {/* Main footer content */}
          <div className="py-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Company info */}
              <div className="space-y-6 lg:col-span-2">
                <div>
                  <h3 className="text-2xl font-bold font-['Outfit'] mb-2">[MARQUE]</h3>
                  <p className="text-gray-300 mb-4">
                    Entreprise de couverture et zinguerie basée à Cugnaux, 
                    spécialisée dans tous travaux de toiture sur Toulouse et l'Occitanie.
                  </p>
                </div>

                {/* Contact info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-blue-400" />
                    <button 
                      onClick={handleCallClick}
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      [TEL]
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <button 
                      onClick={handleEmailClick}
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      contact@[MARQUE].fr
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    <span>[ADRESSE], 31270 Cugnaux</span>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="space-y-6">
                <h4 className="text-lg font-bold font-['Outfit']">Nos services</h4>
                <ul className="space-y-3 text-gray-300">
                  <li>
                    <Link 
                      to="/services"
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      Réfection de toiture
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/services"
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      Zinguerie
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/services"
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      Étanchéité
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/services"
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      Nettoyage & démoussage
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/services"
                      className="hover:text-blue-400 transition-colors duration-200"
                    >
                      Dépannage urgence
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Zone d'intervention */}
              <div className="space-y-6">
                <h4 className="text-lg font-bold font-['Outfit']">Zone d'intervention</h4>
                <ul className="space-y-3 text-gray-300">
                  <li>Toulouse</li>
                  <li>Cugnaux</li>
                  <li>Colomiers</li>
                  <li>Tournefeuille</li>
                  <li>Blagnac</li>
                  <li>Balma</li>
                  <li>Muret</li>
                  <li className="text-blue-400 font-semibold pt-2">
                    + Toute l'Occitanie
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom footer */}
          <div className="border-t border-gray-700 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-400">
                <p>&copy; 2024 [MARQUE]. Tous droits réservés.</p>
                <p className="mt-1">
                  SIRET : [SIRET] • Décennale & RC Pro
                </p>
              </div>

              {/* Legal links */}
              <div className="flex items-center gap-4 text-sm">
                <button className="text-gray-400 hover:text-blue-400 transition-colors duration-200">
                  Mentions légales
                </button>
                <button className="text-gray-400 hover:text-blue-400 transition-colors duration-200">
                  Confidentialité
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;